import {
  ClosureIdentifier,
  DataType,
  OpaqueIdentifier,
  TypedValue,
} from "@sourceacademy/conductor/types";
import { ModuleLoaderRunnerPlugin } from "@sourceacademy/runner-module-loader";
import { ExprNS, StmtNS } from "../../ast-types";
import { RELATIVE_IMPORT_NOT_SUPPORTED_MESSAGE, RuntimeSourceError } from "../../errors";
import { isProperList } from "../../stdlib/linked-list";
import { Context } from "./context";
import { handleRuntimeError } from "./error";
import { appInstr } from "./instrCreator";
import { BuiltinValue, Value } from "./stash";
import { ModuleFunctionGenerator } from "./types";

export class ModuleNotFoundError extends RuntimeSourceError {
  constructor(public readonly moduleName: string) {
    super();
    this.message = `Module "${moduleName}" not found.`;
  }
}

/** `from .foo import x` / `from ..pkg.foo import x` (level > 0): local-file
 * imports are not implemented on the CSE machine yet (see py2js for the
 * supported engine) — reject explicitly rather than treating the dotted
 * path as a conductor module name, which would either 404 confusingly or,
 * worse, collide with an unrelated real module of the same bare name. */
export class RelativeImportNotSupportedError extends RuntimeSourceError {
  constructor(node?: StmtNS.FromImport) {
    super(node);
    this.message = RELATIVE_IMPORT_NOT_SUPPORTED_MESSAGE;
  }
}

export async function loadModules(context: Context, moduleNames: string[]): Promise<void> {
  await Promise.all(
    moduleNames.map(async moduleName => {
      try {
        if (!ModuleLoaderRunnerPlugin.instance) {
          throw new Error("ModuleLoaderRunnerPlugin is not initialized");
        }
        const pluginObj = await ModuleLoaderRunnerPlugin.instance.requestModule(moduleName);
        context.nativeStorage.loadedModules[moduleName] = Object.fromEntries(
          pluginObj?.exports.map(t => [t.symbol, t]) || [],
        );
      } catch {
        handleRuntimeError(context, new ModuleNotFoundError(moduleName));
      }
    }),
  );
}

/** Walks a proper pair()/llist() chain (already confirmed via isProperList) into its actual
 * elements - e.g. { list: [a, { list: [b, { none }] }] } becomes [a, b] - so pythonToModule's
 * "list" case can convert a chain the same way it would a flat literal of the same elements. */
function flattenProperList(value: Value): Value[] {
  const result: Value[] = [];
  let current = value;
  while (current.type === "list") {
    result.push(current.value[0]);
    current = current.value[1];
  }
  return result;
}

export async function pythonToModule(
  context: Context,
  code: string,
  command: ExprNS.Call | undefined,
  value: Value,
): Promise<TypedValue<DataType>> {
  if (!context.evaluator) {
    throw new Error("Context is not properly initialized with evaluator");
  }
  switch (value.type) {
    case "number":
      return { type: DataType.NUMBER, value: value.value };
    case "bool":
      return { type: DataType.BOOLEAN, value: value.value };
    case "string":
      return { type: DataType.CONST_STRING, value: value.value };
    case "none":
      return { type: DataType.EMPTY_LIST, value: null };
    case "list": {
      // Untyped and recursive: per Martin, a pair is just an array of length 2, not a distinct
      // concept - build every Python list (of any length, 2 included, whether it's a fresh
      // literal, a pair()/llist() result, or a module PAIR round-tripped back through Python) the
      // same way, as a flat DataType.ARRAY, recursively converting each element. No more
      // origin-tagging or length-based branching needed - list_to_vec/pair_head/pair_tail (see
      // GenericDataHandler) already read an ARRAY the same as a PAIR/EMPTY_LIST chain, so a module
      // declaring LIST or PAIR still works unchanged.
      //
      // No empty-list special case: EMPTY_LIST is also what Python's None maps to (see
      // moduleToPython's own EMPTY_LIST case), so returning it here for [] would make [] and None
      // collide on the way back out - exactly the kind of ambiguity this whole redesign exists to
      // remove. A genuine 0-length ARRAY round-trips back through moduleToPython's ARRAY case as a
      // real [], not None.
      //
      // A pair()/llist() chain is nested cons cells - llist(a, b) is
      // { list: [a, { list: [b, { none }] }] }, not the flat { list: [a, b] } a literal [a, b]
      // produces - so mapping value.value directly here would turn element 1 of the chain into a
      // nested sub-list instead of the list's real second element. isProperList (true only for a
      // chain that actually terminates in None) tells that case apart from a raw 2-element list
      // like [1, 2] or a module PAIR round-tripped back in, which fall through unchanged below.
      const flatElements = isProperList(value) ? flattenProperList(value) : value.value;
      const elements = await Promise.all(
        flatElements.map(el => pythonToModule(context, code, command, el)),
      );
      const array = await context.evaluator.array_make(DataType.ANY, elements.length, {
        type: DataType.VOID,
        value: undefined,
      });
      for (let i = 0; i < elements.length; i++) {
        await context.evaluator.array_set(
          array as unknown as TypedValue<DataType.ARRAY, DataType.VOID>,
          i,
          elements[i],
        );
      }
      return array;
    }
    case "opaque":
      return { type: DataType.OPAQUE, value: value.value as OpaqueIdentifier };
    case "builtin":
      if ("id" in value.func && typeof value.func.id === "number" && value.func.id !== null) {
        return { type: DataType.CLOSURE, value: value.func.id as ClosureIdentifier<DataType> };
      }
      async function* builtinFunc(...args: TypedValue<DataType>[]): ModuleFunctionGenerator {
        const result = await (value as BuiltinValue).func(
          await Promise.all(args.map(arg => moduleToPython(context, code, command, arg))),
          code,
          command as ExprNS.Call,
          context,
        );
        if (result === undefined) {
          return { type: DataType.VOID, value: undefined };
        }
        if ("next" in result) {
          return yield* result;
        }
        return pythonToModule(context, code, command, result);
      }

      return context.evaluator.closure_make<DataType[], DataType>(
        { returnType: DataType.ANY, args: Array(value.minArgs).fill(DataType.ANY) },
        builtinFunc,
        undefined,
        true,
      );
    case "bigint":
      return { type: DataType.NUMBER, value: Number(value.value) };
    case "closure":
      const value2 = value;
      async function* closureFunc(...args: TypedValue<DataType>[]): ModuleFunctionGenerator {
        context.control.push(appInstr(args.length, command as ExprNS.Call));
        context.stash.push(value2);
        for (const arg of args) {
          context.stash.push(await moduleToPython(context, code, command, arg));
        }
        yield;
        return pythonToModule(context, code, command, context.stash.pop()!);
      }
      const arity =
        (value.closure.node.parameters.findIndex(p => p.isStarred) + 1 ||
          value.closure.node.parameters.length + 1) - 1;
      return context.evaluator.closure_make<DataType[], DataType>(
        {
          returnType: DataType.ANY,
          args: Array(arity).fill(DataType.ANY),
        },
        closureFunc,
        undefined,
        value.closure.node.parameters.some(p => p.isStarred),
      );
    case "complex":
    case "multi_lambda":
    case "error":
    case "function":
      throw new Error("This construct is not supported in module interop");
  }
}

/**
 * Reads a PAIR or ARRAY's elements uniformly: a PAIR is always exactly 2 (head, tail - whatever
 * they are, not necessarily a proper list continuation - e.g. sound's Sound is (wave, duration),
 * a dotted pair whose second element is a plain NUMBER), an ARRAY is however many array_length
 * reports. Deliberately NOT list_to_vec: that walks a chain expecting it to terminate in
 * EMPTY_LIST (a *proper list* invariant), which a raw dotted pair doesn't satisfy - this is a
 * flat "give me this compound value's N elements" read, nothing more.
 */
async function readCompoundElements(
  evaluator: NonNullable<Context["evaluator"]>,
  value: TypedValue<DataType.ARRAY> | TypedValue<DataType.PAIR>,
): Promise<TypedValue<DataType>[]> {
  if (value.type === DataType.PAIR) {
    return [await evaluator.pair_head(value), await evaluator.pair_tail(value)];
  }
  const length = await evaluator.array_length(value);
  return Promise.all(Array.from({ length }, (_, i) => evaluator.array_get(value, i)));
}

export async function moduleToPython(
  context: Context,
  code: string,
  command: ExprNS.Call | undefined,
  value: TypedValue<DataType>,
): Promise<Value> {
  if (!context.evaluator) {
    throw new Error("Context is not properly initialized with evaluator");
  }
  switch (value.type) {
    case DataType.NUMBER:
      return { type: "number", value: value.value }; // TODO: handle bigint
    case DataType.INTEGER:
      // py-slang never produces DataType.INTEGER itself (see pythonToModule's "bigint" case
      // above) - per Martin, integers stay out of the module interface entirely, numbers crossing
      // a module boundary are always floats. Only here for switch exhaustiveness over conductor's
      // DataType enum.
      return { type: "number", value: Number(value.value) };
    case DataType.BOOLEAN:
      return { type: "bool", value: value.value };
    case DataType.CONST_STRING:
      return { type: "string", value: value.value };
    case DataType.VOID:
    case DataType.EMPTY_LIST:
      return { type: "none" };
    case DataType.OPAQUE:
      return { type: "opaque", value: value.value };
    case DataType.ARRAY:
    case DataType.PAIR: {
      // Untyped and recursive, uniformly for both: per Martin, a PAIR is just a 2-element array,
      // not a distinct concept - one shared conversion, not a separate case per DataType.
      const elements = await readCompoundElements(context.evaluator, value);
      return {
        type: "list",
        value: await Promise.all(elements.map(el => moduleToPython(context, code, command, el))),
      };
    }
    case DataType.CLOSURE:
      async function* builtinGenerator(
        args: Value[],
        code: string,
        command: ExprNS.Call,
        context: Context,
      ): ModuleFunctionGenerator {
        const result = yield* context.evaluator!.closure_call(
          value as TypedValue<DataType.CLOSURE>,
          await Promise.all(args.map(arg => pythonToModule(context, code, command, arg))),
          DataType.ANY,
        );
        return result;
      }
      builtinGenerator.id = value.value;
      return {
        type: "builtin",
        minArgs: await context.evaluator.closure_arity(value),
        name: "closure",
        func: builtinGenerator,
      };
  }
}
